import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Typography,
    Button,
    TextField,
    Tabs,
    Tab,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip
} from '@mui/material';
import ContentPasteSearchOutlinedIcon from '@mui/icons-material/ContentPasteSearchOutlined';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import HistoryIcon from '@mui/icons-material/History';
import { Breadcrumb } from 'components';
import { DataGrid, GridRenderCellParams, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router';
import { getAllOnboardRequestForReviewers, getOnboardRequestDetails } from 'services';

interface ClientData {
    id: number;
    businessName: string;
    contactName: string;
    email: string;
    state: string;
    status: string;
    phone: string;
    createdDate: string;
    userVersions?: UserVersion[];
}

interface UserVersion {
    id?: number;
    businessName?: string;
    contactName?: string;
    email?: string;
    state?: string;
    status?: string;
    phone?: string;
    createdDate?: string;
    savedClientOnboardRequest?: any;
}

interface PaginationModel {
    data: ClientData[];
    total: number;
    isLoading: boolean;
    page: number;
    pageSize: number;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`client-tabpanel-${index}`}
            aria-labelledby={`client-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 2 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export const NewClientRequest: React.FC = () => {
    const navigate = useNavigate();

    const [tabValue, setTabValue] = useState(0);
    const [searchValue, setSearchValue] = useState<string>('');
    const [contactName, setContactName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedRow, setSelectedRow] = useState<ClientData | null>(null);
    const [isUserVersionDialogOpen, setIsUserVersionDialogOpen] = useState(false);
    const [selectedUserVersions, setSelectedUserVersions] = useState<UserVersion[]>([]);
    const [paginationModel, setPaginationModel] = useState<PaginationModel>({
        data: [],
        total: 0,
        isLoading: false,
        page: 1,
        pageSize: 10
    });

    const isMenuOpen = Boolean(menuAnchorEl);

    const openActionMenu = (event: React.MouseEvent<HTMLElement>, row: ClientData) => {
        setMenuAnchorEl(event.currentTarget);
        setSelectedRow(row);
    };

    const closeActionMenu = () => {
        setMenuAnchorEl(null);
        setSelectedRow(null);
    };

    const openUserVersionDialog = () => {
        setSelectedUserVersions(selectedRow?.userVersions || []);
        setIsUserVersionDialogOpen(true);
        closeActionMenu();
    };

    const closeUserVersionDialog = () => {
        setIsUserVersionDialogOpen(false);
        setSelectedUserVersions([]);
    };

    const columns: GridColDef[] = [
        {
            field: 'businessName',
            headerName: 'Company / Business Name',
            width: 220,
            sortable: true,
            renderCell: (params: GridRenderCellParams) => (
                <Tooltip title={params.row?.businessName || ''} arrow>
                    <Typography variant="body2" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                        {params.row?.businessName || ''}
                    </Typography>
                </Tooltip>
            ) 
        },
        {
            field: 'contactName',
            headerName: 'Contact Name',
            width: 160,
            sortable: true,
            renderCell: (params: GridRenderCellParams) => (
                <Tooltip title={params.row?.contactName || ''} arrow>
                    <Typography variant="body2" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                        {params.row?.contactName || ''}
                    </Typography>
                </Tooltip>
            )   
        },
        {
            field: 'email',
            headerName: 'Email',
            width: 220,
            sortable: true,
            renderCell: (params: GridRenderCellParams) => (
                <Tooltip title={params.row?.email || ''} arrow>
                    <Typography variant="body2" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>  
                        {params.row?.email || ''}
                    </Typography>
                </Tooltip>
            )
        },
        {
            field: 'requestedOn',
            headerName: 'Requested On',
            width: 120,
            sortable: true
        },

        // Show only for tab 2
        ...(tabValue === 2
            ? [
                {
                    field: 'approvedOn',
                    headerName: 'Approved On',
                    width: 120,
                    sortable: true
                } as GridColDef,
                {
                    field: 'approverName',
                    headerName: 'Approver',
                    width: 120,
                    sortable: true,
                    renderCell: (params: GridRenderCellParams) => (
                        <Tooltip title={params.row?.approverName || ''} arrow>
                            <Typography variant="body2" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                                {params.row?.approverName || ''}
                            </Typography>
                        </Tooltip>
                    )
                } as GridColDef,
            ]
            : []),

        {
            field: 'reviewerNotes',
            headerName: 'Notes',
            width: 240,
            renderCell: (params: GridRenderCellParams) => (
                <Tooltip title={params.row?.reviewerNotes || ''} arrow>
                    <Typography
                        variant="body2"
                        noWrap
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                        }}
                    >
                        {params.row?.reviewerNotes}
                    </Typography>
                </Tooltip>
            )
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 140,
            sortable: true,
            renderCell: (params: GridRenderCellParams) => {
                const getStatusColor = (status: string) => {
                    switch (status) {
                        case 'Approved':
                            return '#4caf50';
                        case 'Rejected':
                            return '#f44336';
                        case 'AwaitingApproval':
                            return '#f58025';
                        case 'Requested':
                            return '#003883';
                        default:
                            return '#9297c5';
                    }
                };

                return (
                    <Box
                        sx={{
                            backgroundColor: getStatusColor(params.value),
                            color: 'white',
                            padding: '8px',
                            borderRadius: '40px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            minWidth: '100px'
                        }}
                    >
                        {params.value}
                    </Box>
                );
            }
        },
        {
            field: 'action',
            headerName: 'Action',
            width: 80,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <IconButton
                    size="small"
                    onClick={(event) => openActionMenu(event, params.row as ClientData)}
                    sx={{ color: '#003883' }}
                >
                    <MoreHorizIcon fontSize="small" />
                </IconButton>
            )
        }
    ];

    const userVersionColumns: GridColDef[] = [
        {
            field: 'businessName',
            headerName: 'Company / Business Name',
            width: 250,
            sortable: false,
            valueGetter: (params) => params.row?.companyName || ''
        },
        {
            field: 'contactName',
            headerName: 'Contact Name',
            width: 200,
            sortable: false,
            valueGetter: (params) => params.row?.contactName || params.row?.savedClientOnboardRequest?.clientOnboardRequest?.fullName || ''
        },
        {
            field: 'email',
            headerName: 'Email',
            width: 280,
            sortable: false,
            valueGetter: (params) => params.row?.userEmail || ''
        },
        {
            field: 'status',
            headerName: 'Version',
            width: 150,
            sortable: false,
            valueGetter: (params) => params.row?.versionType || ''
        },
        {
            field: 'action',
            headerName: 'Action',
            width: 120,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleUserVersionView(params.row as UserVersion)}
                    sx={{
                        fontSize: '0.75rem',
                        padding: '6px',
                        textTransform: 'none',
                        fontWeight: 500,
                        minWidth: '100px'
                    }}
                >
                    View
                </Button>
            )
        }
    ];

    const jwt: any = localStorage.getItem('token');
    const labUserCheck: any = jwt ? JSON.parse(atob(jwt.split('.')[1])) : null;

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        // Reset pagination when switching tabs
        setPaginationModel(prev => ({
            ...prev,
            page: 1,
            data: [],
            total: 0
        }));
        setSearchValue('');
        setContactName('');
        setEmail('');
    };

    const submitForm = (e: React.FormEvent) => {
        e.preventDefault();
        setPaginationModel(prev => ({
            ...prev,
            page: 1,
        }));
        loadGridData(1, searchValue, contactName, email, tabValue);
    };

    useEffect(() => {
        setPaginationModel(old => ({ ...old, isLoading: true }));
        loadGridData(1, '', '', '', tabValue);
    }, [tabValue]);

    const loadGridData = (page: number, search: string, contact: string, emailParam: string, isRequested: any) => {
        const tabPosition = isRequested === 0 ? 0 : isRequested === 1 ? -1 : 1;
        getAllOnboardRequestForReviewers(page, search, contact, emailParam, tabPosition).then((res: any) => {
            if (res.status === 200) {
                const responseData = res.data?.clientOnboardRequestsListDTOs;
                setPaginationModel(old => ({
                    ...old,
                    isLoading: false,
                    page: page,
                    data: responseData,
                    total: res.data?.totalRequests || 0
                }));
            }
        }).catch((error) => {
            setPaginationModel(old => ({ ...old, isLoading: false }));
        });
    };

    const clearTextBox = () => {
        setSearchValue('');
        setContactName('');
        setEmail('');
        setPaginationModel(prev => ({
            ...prev,
            data: [],
            total: 0,
            page: 1,
            isLoading: true
        }));
        loadGridData(1, '', '', '', tabValue);
    };

    const handleReview = (id: number, tabValue: number) => {
        getOnboardRequestDetails(id).then((res: any) => {
            if (res.status === 200) {
                navigate(`/home/review-client-request`, { state: { requestData: res.data, tabValue: tabValue, id: id } });
            }
        }).catch((error) => {
            // Handle error if needed
        });
    };

    const handleMenuPrimaryAction = () => {
        if (!selectedRow) {
            return;
        }

        closeActionMenu();
        handleReview(selectedRow.id, tabValue);
    };

    const handleUserVersionView = (userVersion: UserVersion) => {
        const requestData = userVersion?.savedClientOnboardRequest;

        if (!requestData) {
            return;
        }

        navigate(`/home/review-client-request`, {
            state: {
                requestData,
                tabValue,
                isUserVersion: true
            }
        });
    };

    const handlePageChange = (newPage: number) => {
        setPaginationModel(old => ({
            ...old,
            isLoading: true
        }));
        loadGridData(newPage + 1, searchValue, contactName, email, tabValue);
    };

    const permissions: string[] = Array.isArray(labUserCheck?.Permission)
        ? labUserCheck.Permission
        : labUserCheck?.Permission
            ? [labUserCheck.Permission]
            : [];

    const internalHomePath = permissions.includes('StandardQuotes')
        ? '/home/viewQuote'
        : permissions.includes('ManageClientUsers')
            ? '/home/admin'
            : '';

    const internalAdminPath = permissions.includes('ManageClientUsers')
        ? '/home/admin'
        : '';

    let pathChange = (labUserCheck && (labUserCheck?.UserType === 'Internal')) ? internalHomePath : '/home';

    let nav: any = [
        { name: 'Home', path: pathChange },
        ...(permissions.includes('ManageClientUsers') ? [{ name: 'Admin', path: internalAdminPath }] : []),
        { name: 'New Client Request', path: '/home/new-client-request' }
    ];

    const renderDataGrid = () => (
        <DataGrid
            autoHeight
            sx={{
                '.MuiDataGrid-columnHeaders': {
                    backgroundColor: '#003883',
                    color: '#fff'
                },
                '.MuiDataGrid-menuIcon .MuiSvgIcon-root': {
                    color: '#fff'
                },
                '.MuiDataGrid-iconButtonContainer .MuiSvgIcon-root': {
                    color: '#fff'
                }
            }}
            columns={columns}
            rows={paginationModel.data}
            rowCount={paginationModel.total}
            loading={paginationModel.isLoading}
            page={paginationModel.page - 1}
            pageSize={10}
            rowsPerPageOptions={[10]}
            pagination
            paginationMode="server"
            onPageChange={handlePageChange}
            disableSelectionOnClick
        />
    );

    const renderUserVersionGrid = () => (
        <DataGrid
            autoHeight
            sx={{
                '.MuiDataGrid-columnHeaders': {
                    backgroundColor: '#003883',
                    color: '#fff'
                },
                '.MuiDataGrid-menuIcon .MuiSvgIcon-root': {
                    color: '#fff'
                },
                '.MuiDataGrid-iconButtonContainer .MuiSvgIcon-root': {
                    color: '#fff'
                }
            }}
            columns={userVersionColumns}
            rows={selectedUserVersions.map((version, index) => ({
                ...version,
                id: version.id ?? version.savedClientOnboardRequest?.clientOnboardRequest?.id ?? index + 1
            }))}
            disableSelectionOnClick
            hideFooter
        />
    );

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', cursor: 'pointer' }}>
                <Breadcrumb navList={nav} />
            </div>
            <Typography variant="h6" gutterBottom align='left' mb={2}>
                New Client Request
            </Typography>
            <Menu
                anchorEl={menuAnchorEl}
                open={isMenuOpen}
                onClose={closeActionMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={handleMenuPrimaryAction}>
                    {tabValue === 0 ? <RateReviewIcon fontSize="small" style={{ marginRight: 8 }} /> : <VisibilityIcon fontSize="small" style={{ marginRight: 8 }} />}
                    {tabValue === 0 ? 'Review' : 'View'}
                </MenuItem>
                <MenuItem onClick={openUserVersionDialog}>
                    <HistoryIcon fontSize="small" style={{ marginRight: 8 }} />
                    User Versions
                </MenuItem>
            </Menu>

            <Dialog open={isUserVersionDialogOpen} onClose={closeUserVersionDialog} maxWidth="lg" fullWidth>
                <DialogTitle>User Versions</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        {renderUserVersionGrid()}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeUserVersionDialog}>Close</Button>
                </DialogActions>
            </Dialog>
            <form onSubmit={submitForm}>
                <Grid container spacing={2} mt={1} mb={3} style={{ backgroundColor: '#f5f5f5', marginLeft: '0px', padding: '16px', borderRadius: '6px', width: '100%' }}>
                    <Grid item xs={3} sm={6} md={3} style={{ padding: '0px 8px' }}>
                        <TextField
                            id="search-company"
                            sx={{
                                width: '100%',
                                '& .MuiFormLabel-root': {
                                    fontSize: '12px',
                                },
                            }}
                            type="text"
                            label="Search Company / Business Name"
                            aria-describedby="outlined-weight-helper-text1"
                            value={searchValue}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setSearchValue(ev.target.value)}
                            inputProps={{
                                'aria-label': 'search-company',
                            }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={3} sm={6} md={3} style={{ padding: '0px 8px' }}>
                        <TextField
                            id="contact-name"
                            sx={{
                                width: '100%',
                                '& .MuiFormLabel-root': {
                                    fontSize: '12px',
                                },
                            }}
                            type="text"
                            label="Contact Name"
                            aria-describedby="outlined-weight-helper-text2"
                            value={contactName}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setContactName(ev.target.value)}
                            inputProps={{
                                'aria-label': 'contact-name',
                            }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={3} sm={6} md={3} style={{ padding: '0px 8px' }} sx={{ mt: { xs: 2, sm: 1, md: 0 } }}>
                        <TextField
                            id="email"
                            sx={{
                                width: '100%',
                                '& .MuiFormLabel-root': {
                                    fontSize: '12px',
                                },
                            }}
                            type="text"
                            label="Email Address"
                            aria-describedby="outlined-weight-helper-text3"
                            value={email}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setEmail(ev.target.value)}
                            inputProps={{
                                'aria-label': 'email',
                            }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={3} sm={6} md={3} style={{ padding: '0px 8px' }}>
                        <Box display="flex" alignItems="center" sx={{ mt: { xs: 2, sm: 1, md: 0 } }}>
                            <Button variant="contained" type="submit" color="primary" startIcon={<ContentPasteSearchOutlinedIcon />}>
                                Search
                            </Button>
                            &nbsp;&nbsp;
                            <Button variant="outlined" color="primary" onClick={clearTextBox} startIcon={<ClearOutlinedIcon />}>
                                Clear All
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>

            {/* Tabs Section */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    sx={{
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '12px'
                        },
                        '& .Mui-selected': {
                            color: '#003883'
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#003883'
                        }
                    }}
                >
                    <Tab label="Inprogress" />
                    <Tab label="Redraft" />
                    <Tab label="Complete" />
                </Tabs>
            </Box>

            {/* Tab Panels with DataGrid */}
            <TabPanel value={tabValue} index={0}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={12}>
                        {renderDataGrid()}
                    </Grid>
                </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={12}>
                        {renderDataGrid()}
                    </Grid>
                </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={12}>
                        {renderDataGrid()}
                    </Grid>
                </Grid>
            </TabPanel>
        </div>
    );
};

export default NewClientRequest;